import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { supabase } from '../../../helpers/Supabase';
import { DelegateService } from '../../../services/delegateService';
import Loader from '../../Loader/Loader';
import '../../../styles/components/DelegateSignup.scss';

interface DelegateSignupProps {}

const DelegateSignup: React.FC<DelegateSignupProps> = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState<'weak' | 'medium' | 'strong'>('weak');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const handleResendInvitation = async () => {
    if (!invitationData?.email) return;
    
    setIsResending(true);
    try {
      // Get URL parameters
      const urlParams = new URLSearchParams(location.search);
      const email = urlParams.get('email');
      const type = urlParams.get('type');
      const ids = urlParams.get('ids');

      if (!email || !type || !ids) {
        alert('Invalid invitation parameters. Please contact your administrator.');
        return;
      }

      if (type === 'client') {
        // Resend client delegate invitation
        const { data: delegate } = await supabase
          .from('client_delegates')
          .select('*, clients(name)')
          .eq('id', ids)
          .eq('email', email)
          .single();

        if (delegate) {
          // Re-send the invitation email
          // invitationUrl for resend: `${window.location.origin}/delegate-signup?email=...&type=client&ids=${ids}`
          // You would need to implement this in your EmailService
          // await EmailService.sendDelegateInvite(
          //   { email: delegate.email, name: delegate.name, role: delegate.role },
          //   delegate.clients?.name || 'Your Company',
          //   undefined,
          //   invitationUrl
          // );
          
          alert('Invitation has been resent to your email address.');
        }
      } else if (type === 'store') {
        // Resend store delegate invitation
        const storeIds = ids.split(',');
        const { data: delegates } = await supabase
          .from('store_delegates')
          .select('*, stores(store_name, clients(name))')
          .eq('email', email)
          .in('store_id', storeIds);

        if (delegates && delegates.length > 0) {
          // storeNames, clientName for resend: delegates.map(...), delegates[0]?.stores?.clients?.name
          // You would need to implement this in your EmailService
          // await EmailService.sendDelegateInvite(
          //   { email: delegates[0].email, name: delegates[0].name, role: delegates[0].role },
          //   clientName,
          //   storeNames,
          //   `${window.location.origin}/delegate-signup?email=${encodeURIComponent(email)}&type=store&ids=${ids}`
          // );
          
          alert('Invitation has been resent to your email address.');
        }
      }
    } catch (error) {
      console.error('Error resending invitation:', error);
      alert('Failed to resend invitation. Please contact your administrator.');
    } finally {
      setIsResending(false);
    }
  };

  useEffect(() => {
    const handleInvitation = async () => {
      try {
        // Check for Supabase error parameters in URL hash
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const error = hashParams.get('error');
        const errorCode = hashParams.get('error_code');
        const errorDescription = hashParams.get('error_description');

        if (error) {
          // Handle Supabase errors
          let errorMessage = 'An error occurred with your invitation link.';
          
          if (errorCode === 'otp_expired') {
            errorMessage = 'Your invitation link has expired. Please contact your administrator for a new invitation.';
          } else if (errorCode === 'access_denied') {
            errorMessage = 'Access denied. The invitation link may be invalid or expired.';
          } else if (errorDescription) {
            errorMessage = decodeURIComponent(errorDescription);
          }
          
          setError(errorMessage);
          setIsLoading(false);
          return;
        }

        // Get URL parameters
        const urlParams = new URLSearchParams(location.search);
        const email = urlParams.get('email');
        const type = urlParams.get('type'); // 'client' or 'store'
        const ids = urlParams.get('ids'); // delegate record IDs

        if (!email || !type) {
          setError('Invalid invitation link. Please contact the administrator.');
          setIsLoading(false);
          return;
        }

        // Check if user is already authenticated
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        
        if (user && !userError) {
          // Check if the logged-in user's email matches the invitation email
          if (user.email === email) {
            setIsAuthenticated(true);
          } else {
            // User is logged in with different email - they need to log out first
            setError('You are logged in with a different email address. Please log out and use the email address from your invitation.');
            setIsLoading(false);
            return;
          }
        } else {
          // User needs to create account or sign in
          setIsAuthenticated(false);
        }

        // Verify the invitation is valid
        let delegateData;
        if (type === 'client') {
          const { data, error } = await supabase
            .from('client_delegates')
            .select('*, clients(name)')
            .eq('email', email)
            .eq('is_active', true)
            .single();

          if (error || !data) {
            setError('Invitation not found or has expired. Please contact your administrator for a new invitation.');
            setIsLoading(false);
            return;
          }
          delegateData = data;
        } else if (type === 'store') {
          const storeIds = ids?.split(',') || [];
          const { data, error } = await supabase
            .from('store_delegates')
            .select('*, stores(store_name, clients(name))')
            .eq('email', email)
            .in('store_id', storeIds)
            .eq('is_active', true);

          if (error || !data || data.length === 0) {
            setError('Invitation not found or has expired. Please contact your administrator for a new invitation.');
            setIsLoading(false);
            return;
          }
          delegateData = data[0]; // Use first one for display
        }

        setInvitationData({
          email,
          type,
          ids,
          delegateData,
          user
        });

        setIsLoading(false);
      } catch (error: any) {
        console.error('Error handling invitation:', error);
        setError('Failed to load invitation. Please try again.');
        setIsLoading(false);
      }
    };

    handleInvitation();
  }, [location]);

  const checkPasswordStrength = (password: string) => {
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const isLongEnough = password.length >= 8;

    const score = [hasLower, hasUpper, hasNumber, hasSpecial, isLongEnough].filter(Boolean).length;
    
    if (score >= 4) return 'strong';
    if (score >= 3) return 'medium';
    return 'weak';
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const createUserAccount = async () => {
    // Create a new user account with the invitation email
    const { data, error } = await supabase.auth.signUp({
      email: invitationData.email,
      password: password,
    });

    if (error) {
      throw error;
    }

    return data.user;
  };

  const handleAcceptInvitation = async () => {
    if (!invitationData || !acceptedTerms) return;

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (passwordStrength === 'weak') {
      setError('Please choose a stronger password.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      let currentUser = invitationData.user;

      // If user is not authenticated, create account first
      if (!isAuthenticated) {
        currentUser = await createUserAccount();
        
        // Wait a moment for the account to be created
        await new Promise(resolve => setTimeout(resolve, 1000));
      } else {
        // Update existing user's password
        const { error: passwordError } = await supabase.auth.updateUser({
          password: password
        });

        if (passwordError) {
          throw passwordError;
        }
      }

      // Link the user to the delegate records
      if (invitationData.type === 'client') {
        await DelegateService.linkUserToClientDelegate(
          currentUser.id,
          invitationData.delegateData.id
        );
      } else if (invitationData.type === 'store') {
        const storeIds = invitationData.ids?.split(',') || [];
        await DelegateService.linkUserToStoreDelegates(
          currentUser.id,
          storeIds
        );
      }

      // Mark invitation as accepted
      await DelegateService.acceptInvitation(invitationData.delegateData.id, invitationData.type);

      // Redirect to dashboard
      history.push('/dashboard');
    } catch (error: any) {
      console.error('Error accepting invitation:', error);
      setError(error.message || 'Failed to accept invitation. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getClientName = () => {
    if (invitationData?.type === 'client') {
      return invitationData.delegateData.clients?.name || 'the company';
    } else if (invitationData?.type === 'store') {
      return invitationData.delegateData.stores?.clients?.name || 'the company';
    }
    return 'the dashboard';
  };

  const getStoreNames = () => {
    if (invitationData?.type === 'store') {
      return invitationData.ids?.split(',').length || 0;
    }
    return 0;
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error && !invitationData) {
    const isExpiredLink = error.includes('expired') || error.includes('invalid');
    
    return (
      <div className="delegate-signup-error">
        <div className="error-content">
          <h2>❌ Invitation Error</h2>
          <p>{error}</p>
          <div className="error-actions">
            {error.includes('different email address') && (
              <button 
                onClick={async () => {
                  await supabase.auth.signOut();
                  window.location.reload();
                }}
                className="logout-btn"
              >
                Logout & Try Again
              </button>
            )}
            {isExpiredLink && (
              <button 
                onClick={() => {
                  // You could implement a "request new invitation" feature here
                  window.open('mailto:support@vivvy.ai?subject=Delegate Invitation Expired&body=Hi, my delegate invitation link has expired. Please send me a new one.', '_blank');
                }}
                className="contact-btn"
              >
                Contact Support
              </button>
            )}
            {isExpiredLink && invitationData?.email && (
              <button 
                onClick={handleResendInvitation}
                disabled={isResending}
                className="resend-btn"
              >
                {isResending ? 'Resending...' : 'Resend Invitation'}
              </button>
            )}
            <button onClick={() => history.push('/login')}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="delegate-signup">
      <div className="signup-container">
        <div className="signup-header">
          <img src="/logo.png" alt="Vivvy" className="logo" />
          <h1>Accept Your Invitation</h1>
          <p>Complete your account setup to access {getClientName()}</p>
        </div>

        <div className="invitation-details">
          <h3>Invitation Details</h3>
          <div className="detail-item">
            <span>Email:</span>
            <span>{invitationData?.email}</span>
          </div>
          <div className="detail-item">
            <span>Role:</span>
            <span>{invitationData?.delegateData?.role}</span>
          </div>
          <div className="detail-item">
            <span>Access Type:</span>
            <span>{invitationData?.type === 'client' ? 'Company-wide' : 'Store-specific'}</span>
          </div>
          {invitationData?.type === 'store' && (
            <div className="detail-item">
              <span>Stores:</span>
              <span>{getStoreNames()} store(s)</span>
            </div>
          )}
        </div>

        <div className="password-section">
          <h3>{isAuthenticated ? 'Update Your Password' : 'Create Your Password'}</h3>
          
          <div className="input-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={handlePasswordChange}
              placeholder="Enter your password"
              className={passwordStrength}
            />
            <div className={`password-strength ${passwordStrength}`}>
              <div className="strength-bar">
                <div className={`strength-fill ${passwordStrength}`}></div>
              </div>
              <span className="strength-text">
                {passwordStrength === 'weak' && 'Weak'}
                {passwordStrength === 'medium' && 'Medium'}
                {passwordStrength === 'strong' && 'Strong'}
              </span>
            </div>
          </div>

          <div className="input-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              className={password !== confirmPassword && confirmPassword ? 'error' : ''}
            />
            {password !== confirmPassword && confirmPassword && (
              <span className="error-text">Passwords do not match</span>
            )}
          </div>
        </div>

        <div className="terms-section">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
            <span className="checkmark"></span>
            I accept the invitation and agree to the terms of service
          </label>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <button
          className="accept-button"
          onClick={handleAcceptInvitation}
          disabled={!acceptedTerms || !password || !confirmPassword || password !== confirmPassword || passwordStrength === 'weak' || isProcessing}
        >
          {isProcessing ? 'Accepting Invitation...' : 'Accept Invitation'}
        </button>

        <div className="signup-footer">
          <p>Already have an account? <a href="/login">Sign in</a></p>
        </div>
      </div>
    </div>
  );
};

export default DelegateSignup; 